import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  ButtonBase,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Search, Users, UserRound } from "lucide-react";
import {
  PerformanceOverviewData,
  PerformanceViewMode,
  PerformanceMemberRow,
  PerformanceTeam,
} from "./types";
import { DashboardSectionDivider, useActivateDashboardSection } from "../DashboardSectionDivider";

interface PerformanceOverviewProps {
  data: PerformanceOverviewData;
  title?: string;
  subtitle?: string;
}

const MODE_OPTIONS: {
  value: PerformanceViewMode;
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: "team", label: "Team", icon: <Users size={12} strokeWidth={2.2} /> },
  {
    value: "individual",
    label: "Individual",
    icon: <UserRound size={12} strokeWidth={2.2} />,
  },
];

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

const headerCellSx = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "11px",
  fontWeight: 600,
  color: "#6b7280",
  backgroundColor: "#f8fafc",
  borderBottom: "1px solid #e5e7eb",
  borderRight: "none",
  py: 1,
  px: 1.35,
  whiteSpace: "nowrap" as const,
  letterSpacing: "0.01em",
};

const bodyCellSx = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  color: "#111827",
  borderBottom: "1px solid #f1f5f9",
  borderRight: "none",
  py: 1.15,
  px: 1.35,
};

/** Vertical column markings between metrics */
const colMark = {
  borderRight: "1px solid #d4c8c9",
};

const MetricChip: React.FC<{
  value: number | string;
  tone?: "neutral" | "success" | "danger";
}> = ({ value, tone = "neutral" }) => {
  const styles =
    tone === "success"
      ? { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" }
      : tone === "danger"
        ? { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" }
        : { bg: "#f8fafc", color: "#334155", border: "#e2e8f0" };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 40,
        px: 0.9,
        py: 0.3,
        borderRadius: "8px",
        backgroundColor: styles.bg,
        border: `1px solid ${styles.border}`,
        fontFamily: "'Poppins', sans-serif",
        fontSize: "11.5px",
        fontWeight: 700,
        color: styles.color,
      }}
    >
      {value}
    </Box>
  );
};

const autocompleteSx = {
  width: { xs: "100%", sm: 240 },
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Poppins', sans-serif",
    fontSize: "11.5px",
    backgroundColor: "#ffffff",
    borderRadius: "9px",
    minHeight: 32,
    py: 0,
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#d2363a88" },
    "&.Mui-focused fieldset": {
      borderColor: "#d2363a",
      borderWidth: "1.5px",
    },
  },
};

const listboxPaperSx = {
  mt: 0.5,
  borderRadius: "10px",
  border: "1px solid #e8eaed",
  boxShadow: "0 8px 20px rgba(17,24,39,0.1)",
  overflow: "hidden",
  "& .MuiAutocomplete-listbox": {
    py: 0.4,
    maxHeight: 220,
  },
};

const AvatarChip: React.FC<{ name: string; tone?: "red" | "slate" }> = ({
  name,
  tone = "red",
}) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: "6px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      fontFamily: "'Poppins', sans-serif",
      fontSize: "9px",
      fontWeight: 700,
      color: tone === "red" ? "#d2363a" : "#475569",
      backgroundColor: tone === "red" ? "#fff1f1" : "#f1f5f9",
    }}
  >
    {getInitials(name)}
  </Box>
);

export const PerformanceOverview: React.FC<PerformanceOverviewProps> = ({
  data,
  title = "Performance Overview",
  subtitle = "Team and individual sales performance",
}) => {
  const { activateSection } = useActivateDashboardSection();
  const [mode, setMode] = useState<PerformanceViewMode>("team");
  const [selectedTeamId, setSelectedTeamId] = useState(
    data.teams[0]?.id || "",
  );
  const [selectedMemberId, setSelectedMemberId] = useState(
    data.members[0]?.id || "",
  );

  React.useEffect(() => {
    if (data.teams.length > 0) {
      const exists = data.teams.some((team) => team.id === selectedTeamId);
      if (!exists) {
        setSelectedTeamId(data.teams[0].id);
      }
    } else {
      setSelectedTeamId("");
    }
  }, [data.teams, selectedTeamId]);

  React.useEffect(() => {
    if (data.members.length > 0) {
      const exists = data.members.some((member) => member.id === selectedMemberId);
      if (!exists) {
        setSelectedMemberId(data.members[0].id);
      }
    } else {
      setSelectedMemberId("");
    }
  }, [data.members, selectedMemberId]);

  const selectedTeam =
    data.teams.find((team) => team.id === selectedTeamId) || null;
  const selectedMember =
    data.members.find((member) => member.id === selectedMemberId) || null;

  const teamNameById = useMemo(() => {
    return Object.fromEntries(data.teams.map((team) => [team.id, team.name]));
  }, [data.teams]);

  const tableRows: PerformanceMemberRow[] = useMemo(() => {
    if (mode === "team") {
      return data.members.filter((member) => member.teamId === selectedTeamId);
    }
    return data.members.filter((member) => member.id === selectedMemberId);
  }, [data.members, mode, selectedTeamId, selectedMemberId]);

  const selectionLabel =
    mode === "team"
      ? selectedTeam?.name || "Select team"
      : selectedMember?.name || "Select user";

  const renderFilterOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: { id: string; name: string; teamId?: string },
  ) => {
    const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
      key?: string;
    };
    return (
      <Box
        component="li"
        key={key}
        {...optionProps}
        sx={{
          display: "flex !important",
          alignItems: "center",
          gap: 0.85,
          px: "8px !important",
          py: "6px !important",
          mx: 0.4,
          borderRadius: "8px",
          "&.Mui-focused": { backgroundColor: "#fff5f5 !important" },
          '&[aria-selected="true"]': { backgroundColor: "#fef2f2 !important" },
        }}
      >
        <AvatarChip name={option.name} tone={mode === "team" ? "slate" : "red"} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "11.5px",
              fontWeight: 600,
              color: "#111827",
              lineHeight: 1.2,
            }}
          >
            {option.name}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "9.5px",
              color: "#6b7280",
              mt: 0.1,
            }}
          >
            {option.teamId
              ? teamNameById[option.teamId] || "Team"
              : `${data.members.filter((m) => m.teamId === option.id).length} members`}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2.75 }} onClickCapture={() => activateSection("performance")}>
      <DashboardSectionDivider sectionId="performance" />
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "11px",
              color: "#6b7280",
              mt: 0.15,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={`${tableRows.length} ${tableRows.length === 1 ? "record" : "records"}`}
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "10px",
            fontWeight: 600,
            height: 22,
            backgroundColor: "#fff1f1",
            color: "#d2363a",
            border: "1px solid #f7caca",
          }}
        />
      </Box>

      <Box
        sx={{
          borderRadius: "12px",
          border: "1px solid #d4c8c9",
          backgroundColor: "#ffffff",
          overflow: "hidden",
          boxShadow:
            "0 2px 4px rgba(42, 18, 20, 0.08), 0 10px 24px rgba(42, 18, 20, 0.12), 0 1px 0 rgba(255,255,255,0.85) inset",
          transition: "box-shadow 0.35s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.25s ease",
          "@keyframes dashboardPanelIn": {
            from: {
              opacity: 0,
              transform: "translateY(10px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
          animation: "dashboardPanelIn 560ms cubic-bezier(0.22, 1, 0.36, 1) both",
          "&:hover": {
            borderColor: "#c9bbbc",
            boxShadow:
              "0 4px 10px rgba(42, 18, 20, 0.12), 0 14px 32px rgba(90, 24, 28, 0.16), 0 1px 0 rgba(255,255,255,0.9) inset",
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            px: 1.25,
            py: 0.9,
            borderBottom: "1px solid #eef0f3",
            backgroundColor: "#fafbfc",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              p: "2px",
              borderRadius: "999px",
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              gap: "2px",
            }}
          >
            {MODE_OPTIONS.map((option) => {
              const selected = mode === option.value;
              return (
                <ButtonBase
                  key={option.value}
                  onClick={() => setMode(option.value)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.45,
                    px: 1.1,
                    py: 0.4,
                    borderRadius: "999px",
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "11px",
                    fontWeight: selected ? 600 : 500,
                    color: selected ? "#ffffff" : "#4b5563",
                    backgroundColor: selected ? "#d2363a" : "transparent",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: selected ? "#b92d31" : "#f3f4f6",
                    },
                  }}
                >
                  {option.icon}
                  {option.label}
                </ButtonBase>
              );
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Chip
              size="small"
              label={selectionLabel}
              sx={{
                display: { xs: "none", md: "inline-flex" },
                fontFamily: "'Poppins', sans-serif",
                fontSize: "10px",
                fontWeight: 600,
                height: 22,
                maxWidth: 140,
                backgroundColor: "#111827",
                color: "#ffffff",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />

            {mode === "team" ? (
              <Autocomplete
                size="small"
                options={data.teams}
                value={selectedTeam}
                onChange={(_event, value: PerformanceTeam | null) => {
                  if (value) setSelectedTeamId(value.id);
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) => {
                  const query = state.inputValue.trim().toLowerCase();
                  if (!query) return options;
                  return options.filter((option) =>
                    option.name.toLowerCase().includes(query),
                  );
                }}
                popupIcon={null}
                clearOnBlur={false}
                sx={autocompleteSx}
                slotProps={{ paper: { sx: listboxPaperSx } }}
                renderOption={renderFilterOption}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search team..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: "#9ca3af",
                              pl: 0.5,
                              pr: 0.25,
                            }}
                          >
                            <Search size={13} />
                          </Box>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <Autocomplete
                size="small"
                options={data.members}
                value={selectedMember}
                onChange={(_event, value: PerformanceMemberRow | null) => {
                  if (value) setSelectedMemberId(value.id);
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) => {
                  const query = state.inputValue.trim().toLowerCase();
                  if (!query) return options;
                  return options.filter((option) => {
                    const teamName = teamNameById[option.teamId] || "";
                    return (
                      option.name.toLowerCase().includes(query) ||
                      teamName.toLowerCase().includes(query)
                    );
                  });
                }}
                popupIcon={null}
                clearOnBlur={false}
                sx={autocompleteSx}
                slotProps={{ paper: { sx: listboxPaperSx } }}
                renderOption={renderFilterOption}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search user..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: "#9ca3af",
                              pl: 0.5,
                              pr: 0.25,
                            }}
                          >
                            <Search size={13} />
                          </Box>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 0,
            boxShadow: "none",
            maxHeight: 320,
            backgroundColor: "transparent",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={{ ...headerCellSx, ...colMark }}>
                  Members
                </TableCell>
                <TableCell rowSpan={2} align="center" sx={{ ...headerCellSx, ...colMark }}>
                  Enquiry
                </TableCell>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{
                    ...headerCellSx,
                    ...colMark,
                    color: "#111827",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  Quotation
                </TableCell>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{
                    ...headerCellSx,
                    ...colMark,
                    color: "#111827",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  Order
                </TableCell>
                <TableCell rowSpan={2} align="center" sx={headerCellSx}>
                  Conversion rate%
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{ ...headerCellSx, ...colMark, color: "#047857" }}
                >
                  Won
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ ...headerCellSx, ...colMark, color: "#b91c1c" }}
                >
                  Lost
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ ...headerCellSx, ...colMark, color: "#047857" }}
                >
                  From Quotation
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ ...headerCellSx, ...colMark, color: "#334155" }}
                >
                  Direct
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{
                      ...bodyCellSx,
                      color: "#6b7280",
                      py: 3,
                    }}
                  >
                    {mode === "team"
                      ? "No members found for the selected team"
                      : "No data found for the selected user"}
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafbfc",
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: "#fff5f5" },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSx, ...colMark }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.85,
                          minWidth: 0,
                        }}
                      >
                        <AvatarChip name={row.name} />
                        <Typography
                          sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#111827",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {row.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ ...bodyCellSx, ...colMark }}>
                      <MetricChip value={row.enquiry} />
                    </TableCell>
                    <TableCell align="center" sx={{ ...bodyCellSx, ...colMark }}>
                      <MetricChip value={row.quotationWon} tone="success" />
                    </TableCell>
                    <TableCell align="center" sx={{ ...bodyCellSx, ...colMark }}>
                      <MetricChip value={row.quotationLost} tone="danger" />
                    </TableCell>
                    <TableCell align="center" sx={{ ...bodyCellSx, ...colMark }}>
                      <MetricChip value={row.orderFromQuotation} tone="success" />
                    </TableCell>
                    <TableCell align="center" sx={{ ...bodyCellSx, ...colMark }}>
                      <MetricChip value={row.directOrder} />
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 46,
                          px: 0.9,
                          py: 0.3,
                          borderRadius: "999px",
                          backgroundColor:
                            row.conversionRate >= 75
                              ? "#ecfdf5"
                              : row.conversionRate >= 60
                                ? "#fff7ed"
                                : "#fef2f2",
                          border: `1px solid ${
                            row.conversionRate >= 75
                              ? "#a7f3d0"
                              : row.conversionRate >= 60
                                ? "#fde68a"
                                : "#fecaca"
                          }`,
                          color:
                            row.conversionRate >= 75
                              ? "#047857"
                              : row.conversionRate >= 60
                                ? "#c2410c"
                                : "#b91c1c",
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        {row.conversionRate}%
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};



